interface BarData {
  label: string;
  value: number;
  color?: string;
}

interface BarChartProps {
  data: BarData[];
  height?: number;
  barColor?: string;
  unit?: string;
}

export function BarChart({ data, height = 180, barColor = '#3b82f6', unit = 'h' }: BarChartProps) {
  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center" style={{ height }}>
        <span className="text-sm text-muted-foreground">No data</span>
      </div>
    );
  }

  const maxValue = Math.max(...data.map((d) => d.value), 1);
  const padding = { top: 16, right: 8, bottom: 28, left: 40 };
  const chartWidth = Math.max(data.length * 48, 300);
  const chartHeight = height - padding.top - padding.bottom;
  const barWidth = Math.min(32, (chartWidth - padding.left - padding.right) / data.length - 4);

  // Y-axis ticks
  const tickCount = 4;
  const ticks = Array.from(
    { length: tickCount + 1 },
    (_, i) => Math.round((maxValue / tickCount) * i * 10) / 10,
  );

  return (
    <div className="overflow-x-auto">
      <svg width={chartWidth} height={height} viewBox={`0 0 ${chartWidth} ${height}`}>
        {/* Y-axis grid lines + labels */}
        {ticks.map((tick, i) => {
          const y = padding.top + chartHeight - (tick / maxValue) * chartHeight;
          return (
            <g key={i}>
              <line
                x1={padding.left}
                y1={y}
                x2={chartWidth - padding.right}
                y2={y}
                stroke="currentColor"
                strokeOpacity={0.1}
              />
              <text
                x={padding.left - 6}
                y={y + 4}
                textAnchor="end"
                className="fill-muted-foreground text-[10px]"
              >
                {tick}
                {unit}
              </text>
            </g>
          );
        })}

        {/* Bars */}
        {data.map((d, i) => {
          const barHeight = (d.value / maxValue) * chartHeight;
          const x =
            padding.left +
            ((chartWidth - padding.left - padding.right) / data.length) * i +
            ((chartWidth - padding.left - padding.right) / data.length - barWidth) / 2;
          const y = padding.top + chartHeight - barHeight;

          return (
            <g key={i}>
              <rect
                x={x}
                y={y}
                width={barWidth}
                height={Math.max(barHeight, 1)}
                rx={2}
                fill={d.color || barColor}
                opacity={0.85}
              >
                <title>{`${d.label}: ${d.value.toFixed(1)}${unit}`}</title>
              </rect>
              <text
                x={x + barWidth / 2}
                y={height - 8}
                textAnchor="middle"
                className="fill-muted-foreground text-[9px]"
              >
                {d.label}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
